import React, { useEffect, useState } from "react";

const GET_API = "https://eventscons.com/eventbackendnew/api/get-reviews";
const ADD_API = "https://eventscons.com/eventbackendnew/api/add-reviews";
const UPDATE_API = "https://eventscons.com/eventbackendnew/api/update-reviews";
const DELETE_API = "https://eventscons.com/eventbackendnew/api/delete-reviews";

const IMAGE_BASE_URL = "https://eventscons.com/eventbackendnew/storage/app/public/";
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpg", "image/jpeg"];
const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg"];

const initialForm = {
  id: "",
  name: "",
  designation: "",
  text: "",
  rating: "",
  image: null,
};

const TestimonialsReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState("");

  const getImageUrl = (src) => {
    if (!src) return "https://via.placeholder.com/500x300?text=No+Image";
    if (src.startsWith("blob:")) return src;
    if (src.startsWith("http://") || src.startsWith("https://")) return src;
    const cleanPath = src.replace(/^\/+/, "");
    return `${IMAGE_BASE_URL}${cleanPath}`;
  };

  const fetchReviews = async () => {
    try {
      setListLoading(true);

      const res = await fetch(GET_API);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Failed to fetch reviews");
      }

      setReviews(Array.isArray(data) ? data : data?.data || data?.reviews || []);
    } catch (error) {
      console.error("Get reviews error:", error);
      setReviews([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const validateImageFile = (file) => {
    if (!file) return false;

    const fileName = file.name?.toLowerCase() || "";
    const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
      fileName.endsWith(ext)
    );
    const hasValidType = ALLOWED_IMAGE_TYPES.includes(file.type);

    if (!hasValidExtension || !hasValidType) {
      alert("Only PNG, JPG, and JPEG files are allowed. WEBP/AVIF are not accepted.");
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert("Image size must be 2MB or less.");
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      const file = files?.[0] || null;

      if (!file) {
        setForm((prev) => ({
          ...prev,
          image: null,
        }));
        setPreview(editMode ? preview : "");
        return;
      }

      const isValid = validateImageFile(file);

      if (!isValid) {
        e.target.value = "";
        setForm((prev) => ({
          ...prev,
          image: null,
        }));
        setPreview(editMode ? preview : "");
        return;
      }

      setForm((prev) => ({
        ...prev,
        image: file,
      }));

      setPreview(URL.createObjectURL(file));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditMode(false);
    setShowForm(false);
    setPreview("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("designation", form.designation);
      formData.append("text", form.text);
      formData.append("rating", form.rating);

      if (form.image) {
        formData.append("image", form.image);
      }

      let url = ADD_API;

      if (editMode) {
        url = UPDATE_API;
        formData.append("_method", "PUT");
        formData.append("id", form.id);
      }

      const res = await fetch(url, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      console.log(editMode ? "Update response:" : "Add response:", data);

      if (!res.ok || data?.status === false) {
        throw new Error(
          data?.message ||
            (editMode ? "Failed to update review" : "Failed to add review")
        );
      }

      alert(
        data?.message ||
          (editMode
            ? "Review updated successfully"
            : "Review added successfully")
      );
      resetForm();
      fetchReviews();
    } catch (error) {
      console.error(editMode ? "Update error:" : "Add error:", error);
      alert(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id || "",
      name: item.name || "",
      designation: item.designation || "",
      text: item.text || "",
      rating: item.rating || "",
      image: null,
    });

    setPreview(getImageUrl(item.image || ""));
    setEditMode(true);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this review?"
    );

    if (!confirmDelete) return;

    try {
      const res = await fetch(`${DELETE_API}/${id}`, {
        method: "DELETE",
      });

      const data = await res.json();
      console.log("Delete response:", data);

      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || "Failed to delete review");
      }

      alert(data?.message || "Review deleted successfully");
      fetchReviews();
    } catch (error) {
      console.error("Delete error:", error);
      alert(error.message || "Something went wrong");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Testimonials Reviews</h2>
            <p style={styles.subtitle}>
              Manage client feedback, testimonials and review content from here.
            </p>
          </div>

          <button
            style={styles.addButton}
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + Add New Review
          </button>
        </div>

        {showForm && (
          <div style={styles.formCard}>
            <div style={styles.formHeader}>
              <h3 style={styles.cardTitle}>
                {editMode ? "Update Review" : "Add New Review"}
              </h3>
              <button style={styles.closeBtn} onClick={resetForm}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={styles.grid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    required
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Designation</label>
                  <input
                    type="text"
                    name="designation"
                    value={form.designation}
                    onChange={handleChange}
                    placeholder="Enter designation"
                    required
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Rating</label>
                  <input
                    type="number"
                    name="rating"
                    value={form.rating}
                    onChange={handleChange}
                    placeholder="1 to 5"
                    min="1"
                    max="5"
                    required
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Image</label>
                  <input
                    type="file"
                    name="image"
                    accept=".png,.jpg,.jpeg"
                    onChange={handleChange}
                    style={styles.fileInput}
                  />
                  <small style={styles.helpText}>
                    Only PNG, JPG, JPEG allowed. Max size 2MB.
                  </small>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Review Text</label>
                <textarea
                  name="text"
                  value={form.text}
                  onChange={handleChange}
                  placeholder="Write testimonial or review text..."
                  rows="5"
                  required
                  style={styles.textarea}
                />
              </div>

              {preview && (
                <div style={{ marginBottom: "20px" }}>
                  <img
                    src={getImageUrl(preview)}
                    alt="Preview"
                    style={styles.previewImage}
                  />
                </div>
              )}

              <div style={styles.actionRow}>
                <button type="submit" disabled={loading} style={styles.saveBtn}>
                  {loading
                    ? "Please wait..."
                    : editMode
                    ? "Update Review"
                    : "Save Review"}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div style={styles.listSection}>
          <div style={styles.listHeader}>
            <span style={styles.countBadge}>{reviews.length} Total</span>
          </div>

          {listLoading ? (
            <p style={styles.emptyText}>Loading reviews...</p>
          ) : reviews.length === 0 ? (
            <p style={styles.emptyText}>No reviews found.</p>
          ) : (
            <div style={styles.cardGrid}>
              {reviews.map((item) => (
                <div key={item.id} style={styles.reviewCard}>
                  <div style={styles.imageWrap}>
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.name}
                      style={styles.reviewImage}
                    />

                    <div style={styles.overlayBtns}>
                      <button
                        style={styles.iconBtn}
                        onClick={() => handleEdit(item)}
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button
                        style={{ ...styles.iconBtn, background: "#ef4444" }}
                        onClick={() => handleDelete(item.id)}
                        title="Delete"
                      >
                        🗑
                      </button>
                    </div>
                  </div>

                  <div style={styles.reviewContent}>
                    <div style={styles.reviewTop}>
                      <div>
                        <h4 style={styles.reviewName}>{item.name}</h4>
                        <p style={styles.reviewDesignation}>
                          {item.designation}
                        </p>
                      </div>
                      <span style={styles.ratingBadge}>⭐ {item.rating}</span>
                    </div>

                    <p style={styles.reviewText}>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f4f6fb",
    minHeight: "100vh",
    padding: "30px 20px",
    fontFamily: "Inter, sans-serif",
  },
  wrapper: {
    maxWidth: "1400px",
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: "24px",
    padding: "28px",
    boxShadow: "0 10px 40px rgba(15, 23, 42, 0.08)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "20px",
    flexWrap: "wrap",
    marginBottom: "28px",
  },
  title: {
    margin: 0,
    fontSize: "40px",
    fontWeight: 800,
    color: "#0f172a",
    textAlign: "left",
  },
  subtitle: {
    margin: "8px 0 0",
    fontSize: "15px",
    color: "#64748b",
  },
  addButton: {
    background: "#a8141f",
    color: "#fff",
    border: "none",
    borderRadius: "14px",
    padding: "16px 24px",
    fontSize: "20px",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 10px 25px rgba(168, 20, 31, 0.25)",
  },
  formCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "22px",
    padding: "24px",
    marginBottom: "28px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
  },
  formHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  cardTitle: {
    margin: 0,
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
  },
  closeBtn: {
    background: "#f1f5f9",
    border: "none",
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "16px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "18px",
    marginBottom: "18px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#334155",
  },
  input: {
    height: "50px",
    border: "1px solid #dbe3ee",
    borderRadius: "12px",
    padding: "0 14px",
    fontSize: "15px",
    outline: "none",
    background: "#fff",
  },
  fileInput: {
    height: "50px",
    border: "1px solid #dbe3ee",
    borderRadius: "12px",
    padding: "10px 14px",
    fontSize: "14px",
    background: "#fff",
  },
  helpText: {
    marginTop: "6px",
    fontSize: "12px",
    color: "#64748b",
  },
  textarea: {
    border: "1px solid #dbe3ee",
    borderRadius: "12px",
    padding: "14px",
    fontSize: "15px",
    outline: "none",
    resize: "vertical",
    width: "100%",
    background: "#fff",
  },
  previewImage: {
    width: "140px",
    height: "140px",
    objectFit: "cover",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
  },
  actionRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  saveBtn: {
    background: "#a8141f",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    padding: "14px 22px",
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
  },
  cancelBtn: {
    background: "#eef2f7",
    color: "#0f172a",
    border: "none",
    borderRadius: "12px",
    padding: "14px 22px",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
  },
  listSection: {
    marginTop: "8px",
  },
  listHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "12px",
  },
  countBadge: {
    background: "#f1f5f9",
    color: "#334155",
    padding: "8px 14px",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: 700,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "24px",
  },
  reviewCard: {
    background: "#fff",
    borderRadius: "22px",
    overflow: "hidden",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
    border: "1px solid #edf2f7",
  },
  imageWrap: {
    position: "relative",
    height: "240px",
    overflow: "hidden",
  },
  reviewImage: {
    width: "70%",
    objectFit: "contain",
    display: "block",
    margin: "0 auto",
  },
  overlayBtns: {
    position: "absolute",
    top: "16px",
    right: "16px",
    display: "flex",
    gap: "10px",
  },
  iconBtn: {
    width: "46px",
    height: "46px",
    borderRadius: "50%",
    border: "none",
    background: "#a8141f",
    color: "#fff",
    fontSize: "18px",
    cursor: "pointer",
    boxShadow: "0 10px 18px rgba(168, 20, 31, 0.28)",
  },
  reviewContent: {
    padding: "20px",
  },
  reviewTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "14px",
    marginBottom: "14px",
  },
  reviewName: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 700,
    color: "#111827",
  },
  reviewDesignation: {
    margin: "4px 0 0",
    fontSize: "14px",
    color: "#64748b",
  },
  ratingBadge: {
    background: "#fff7ed",
    color: "#c2410c",
    padding: "8px 12px",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  reviewText: {
    margin: 0,
    fontSize: "16px",
    lineHeight: 1.7,
    color: "#475569",
  },
  emptyText: {
    fontSize: "15px",
    color: "#64748b",
    padding: "20px 0",
  },
};

export default TestimonialsReviews;